import { cameraGeometry } from '@/infrastructure/engine3d/camera/cameraGeometry';
import { AAnimatedCamera } from '@/infrastructure/engine3d/camera/AAnimatedCamera';
import { CustomOrbitControls } from '@/infrastructure/engine3d/camera/controls/CustomOrbitControls';
import { DEFAULT_CAMERA_ANIMATION_DURATION } from '@/config';
import { Box3, Object3D, Vector3 } from 'three';
import { Easing, Tween } from '@tweenjs/tween.js';
import type { ICameraControls } from '@/infrastructure/engine3d/camera/controls/ICameraControls';
import type { TCameraParams } from '@/infrastructure/engine3d/camera/TCameraParams';

// Arc-rotate perspective camera with damped controls. Adds
// zoomOnSelection: tween the camera to frame a set of meshes.
class ArcRotateCamera extends AAnimatedCamera {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _zoomToFitAnimation: Tween<any> | null = null;

    protected createControls(rendererDomElement: HTMLCanvasElement): ICameraControls {
        return new CustomOrbitControls(this, rendererDomElement);
    }

    constructor(cameraParams: TCameraParams) {
        super(cameraParams);
        this.initializeControls();
    }

    // The distance at which the selection exactly fills the frame. Every zoom
    // factor is a multiple of it, so exposing it lets callers read the camera's
    // current factor back instead of remembering one — a remembered factor goes
    // stale the moment the user touches the wheel, which OrbitControls handles
    // on its own.
    getFitRadius = (objectSelection: Object3D[]): number => {
        return this._frameSelection(objectSelection).radius;
    };

    private _frameSelection(objectSelection: Object3D[]): { positionY: number; radius: number; target: Vector3 } {
        const box = new Box3();
        for (let i = 0; i < objectSelection.length; i += 1) {
            box.expandByObject(objectSelection[i]);
        }
        const target = new Vector3();
        box.getCenter(target);
        const finalPosition = cameraGeometry.fitCameraToSelection(this, target, objectSelection, 0);
        const spherical = cameraGeometry.sphericalFromCartesian(finalPosition.x, target.y, finalPosition.z);
        return { positionY: finalPosition.y, radius: spherical.radius, target };
    }

    zoomOnSelection = (
        objectSelection: Object3D[],
        offset: number = 0,
        duration: number = DEFAULT_CAMERA_ANIMATION_DURATION,
    ): Promise<void> => {
        const { positionY, radius, target } = this._frameSelection(objectSelection);
        const finalRadius = offset ? radius * offset : radius;
        const anim = { t: 0, radius: this.radius, posY: this.position.y };
        return new Promise<void>((resolve) => {
            const ending = () => {
                this._zoomToFitAnimation = null;
                resolve();
            };
            this._zoomToFitAnimation = new Tween(anim)
                .to({ t: 1, radius: finalRadius, posY: positionY }, duration)
                .onUpdate(() => {
                    this.position.copy(new Vector3(0, 0, anim.radius).applyQuaternion(this.quaternion)).add(this.controls.target);
                    this.controls.target.copy(this.controls.target.clone().lerp(target, anim.t));
                })
                .onComplete(ending)
                .easing(Easing.Quadratic.InOut)
                .start();
        });
    };

    dispose(): void {
        this.controls.dispose();
    }

    render(): void {
        super.render();
        this._zoomToFitAnimation?.update();
    }
}

export { ArcRotateCamera };
